import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { commonMessages } from 'src/features/auth/utils';
import { useListMemberships } from 'src/features/userManagement/api';
import { useRetrieveMembershipValidator } from 'src/features/userManagement/api/useRetrieveMembershipValidator';
import { EditUserModalForm } from 'src/features/userManagement/components/forms/EditUserModalForm.';
import UserStatusChangeModal from 'src/features/userManagement/components/forms/UserStatusChangeModal';
import { CellUsersTableActions } from 'src/features/userManagement/components/table/CellUsersTableActions';
import { type Membership, MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { badgeRoleOptions, statusOptions, userManagementMessages } from 'src/features/userManagement/utils';
import useFilters from 'src/hooks/useFilters';
import useHasPermissions from 'src/hooks/useHasPermissions';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import {
    CellBadge,
    CellIndex,
    DataTable,
    DataTableColumnGenericHeaderDefinition,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { generalMessages } from 'src/utils/messages';

const ClientsTable = () => {
    const { formatMessage } = useIntl();

    const { parkingId } = useParking();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting();
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });

    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isStatusModalOpen, setIsStatusModalOpen] = React.useState(false);
    const [selectedMembership, setSelectedMembership] = React.useState<Membership | null>(null);

    const permissionViewStaff = useHasPermissions([PERMISSION.USERS_VIEW_STAFF]);

    const { data, isLoading, isError, refetch } = useListMemberships({
        parkingId,
        user__is_staff: permissionViewStaff ? undefined : false,
        role: [MEMBERSHIP_ROLES.CLIENT],
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
        options: { enabled: !!parkingId },
    });

    const { data: membershipValidatorSettings } = useRetrieveMembershipValidator({
        parkingId,
        membershipId: selectedMembership?.id || '',
    });

    const openEditMembershipModal = (member: Membership) => {
        setSelectedMembership(member);
        setIsModalOpen(true);
    };

    const openMembershipStatusModal = (member: Membership) => {
        setSelectedMembership(member);
        setIsStatusModalOpen(true);
    };

    const columnHelper = createColumnHelper<Membership>();

    const columns = [
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'index',
            cell: CellIndex,
            maxSize: 45,
            meta: { title: '#' },
        }),
        columnHelper.accessor('user.name', {
            id: 'user__name',
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(userManagementMessages.name),
            },
        }),
        columnHelper.accessor('user.email', {
            id: 'user__email',
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(userManagementMessages.email),
            },
        }),
        columnHelper.accessor('role', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(userManagementMessages.role),
                badgeOptions: badgeRoleOptions,
            },
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(userManagementMessages.status),
                badgeOptions: statusOptions,
            },
        }),
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'actions',
            maxSize: 60,
            cell: (props) => (
                <CellUsersTableActions
                    editMembershipCallback={openEditMembershipModal}
                    membershipStatusCallback={openMembershipStatusModal}
                    userStatusAction={props.row.original.status === 'active' ? 'deactivate' : 'activate'}
                    {...props}
                />
            ),
        }),
    ];

    return (
        <>
            {isModalOpen && selectedMembership && (
                <EditUserModalForm
                    setIsModalOpen={setIsModalOpen}
                    membershipData={selectedMembership}
                    validatorSettings={membershipValidatorSettings}
                />
            )}
            {isStatusModalOpen && selectedMembership && (
                <UserStatusChangeModal
                    isUserStatusChangeModalOpen={isStatusModalOpen}
                    closeUserStatusChangeModal={() => setIsStatusModalOpen(false)}
                    selectedMember={selectedMembership}
                    statusAction={selectedMembership.status == 'active' ? 'deactivate' : 'activate'}
                    refetch={refetch}
                />
            )}
            <DataTable
                columns={columns}
                data={data}
                pagination={pagination}
                sorting={sorting}
                filters={filters}
                search={search}
                semantics={{
                    title: formatMessage(userManagementMessages.clients),
                    entryName: formatMessage(userManagementMessages.roleClient),
                    entryNames: formatMessage(userManagementMessages.clients),
                    placeholder: `${formatMessage(generalMessages.name)}, ${formatMessage(commonMessages.email)}`,
                }}
                isLoading={isLoading}
                isError={isError}
                refetch={refetch}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
            />
        </>
    );
};

export default ClientsTable;
